import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Loader2 } from "lucide-react";
import { api } from "@/lib/auth";
import { toast } from "sonner";

/**
 * AskPapi — botón flotante (FAB) global en TODA la app + drawer para que
 * los hijos hagan preguntas y reciban respuesta con la voz clonada de Papi.
 *
 * Props:
 *  - slug: slug de la receta actual (opcional, da contexto al LLM)
 *  - recipeName: para mostrar en el header del drawer
 *  - variant: "fab" (botón flotante, default) | "inline" (botón normal)
 */
export default function AskPapi({ slug = null, recipeName = "", variant = "fab" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]); // [{q, text, audio_url}]
  const audioRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [history, busy]);

  const send = async () => {
    const question = q.trim();
    if (!question || busy) return;
    setBusy(true);
    setQ("");
    // Optimistic add of question
    setHistory((h) => [...h, { q: question, text: null, audio_url: null }]);
    try {
      const res = await api().post("/ask-papi", { question, slug });
      const { text, audio_url } = res.data || {};
      setHistory((h) => {
        const copy = [...h];
        copy[copy.length - 1] = { q: question, text, audio_url };
        return copy;
      });
      // Auto-play audio (the worker returns either an absolute URL or a data: URL)
      if (audio_url && audioRef.current) {
        audioRef.current.src = audio_url;
        audioRef.current.play().catch(() => {});
      }
    } catch (e) {
      toast.error("Papi no pudo responder. Vuelve a intentar.");
      setHistory((h) => h.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  const playAudio = (url) => {
    if (!url || !audioRef.current) return;
    audioRef.current.src = url;
    audioRef.current.play().catch(() => {});
  };

  return (
    <>
      {/* Trigger button — FAB (default) or inline pill */}
      {variant === "fab" ? (
        <button
          data-testid="ask-papi-open-btn"
          onClick={() => setOpen(true)}
          className="fixed z-40 right-4 bottom-[calc(env(safe-area-inset-bottom,0px)+1rem)] w-14 h-14 rounded-full bg-amber-deep text-cream shadow-[0_14px_28px_-12px_rgba(58,26,5,0.55)] flex items-center justify-center hover:scale-[1.05] active:scale-95 transition"
          aria-label="Pregúntale a Papi"
          title="Pregúntale a Papi"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      ) : (
        <button
          data-testid="ask-papi-open-btn"
          onClick={() => setOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-3 rounded-full bg-amber-deep text-cream font-serif text-sm sm:text-base shadow-[0_10px_24px_-12px_rgba(58,26,5,0.55)] hover:scale-[1.02] active:scale-95 transition"
          aria-label="Pregúntale a Papi"
        >
          <MessageCircle className="w-4 h-4" />
          <span>Pregúntale a Papi</span>
        </button>
      )}

      {open && (
        <div
          className="fixed inset-0 z-[60] bg-cocoa/45 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setOpen(false)}
          data-testid="ask-papi-modal"
        >
          <div
            className="bg-paper border border-line rounded-t-2xl sm:rounded-2xl w-full max-w-lg max-h-[85vh] flex flex-col shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-line">
              <div className="flex items-center gap-3">
                <img src="/alan_chef.jpg" alt="Papi" className="w-11 h-11 rounded-full object-cover ring-2 ring-amber-deep/70" style={{ objectPosition: "62% 35%" }} />
                <div>
                  <p className="font-display italic text-cocoa text-xl leading-none">Pregúntale a Papi</p>
                  {recipeName && <p className="text-xs text-cocoa-soft font-serif italic mt-1 truncate max-w-[230px]">sobre {recipeName}</p>}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-full hover:bg-cream transition"
                aria-label="Cerrar"
                data-testid="ask-papi-close-btn"
              >
                <X className="w-5 h-5 text-cocoa" />
              </button>
            </div>

            {/* Conversation list */}
            <div ref={listRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {history.length === 0 && !busy && (
                <div className="text-center py-6">
                  <p className="font-serif italic text-cocoa-soft text-lg mb-3">
                    Hazle una pregunta a Papi:
                  </p>
                  <div className="space-y-2 max-w-sm mx-auto">
                    {[
                      "¿Con qué reemplazo el merkén?",
                      "¿Qué día cae Yom Kipur en el calendario?",
                      "¿Cuánto es 1/4 de taza?",
                    ].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setQ(s)}
                        data-testid={`ask-papi-suggestion-${s.slice(0,6)}`}
                        className="block w-full text-left text-base font-serif italic text-amber-deep border border-amber-deep/30 rounded-md px-3 py-2.5 hover:bg-amber-deep/10 transition"
                      >
                        "{s}"
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {history.map((m, i) => (
                <div key={i} className="space-y-3">
                  <div className="flex justify-end">
                    <div className="bg-amber-deep text-cream px-4 py-3 rounded-2xl rounded-br-md font-serif text-lg max-w-[85%]" data-testid={`ask-papi-q-${i}`}>
                      {m.q}
                    </div>
                  </div>
                  {m.text === null ? (
                    <div className="flex items-start gap-2">
                      <img src="/alan_chef.jpg" alt="Papi" className="w-10 h-10 rounded-full object-cover" style={{ objectPosition: "62% 35%" }} />
                      <div className="bg-cream border border-line px-4 py-3 rounded-2xl rounded-bl-md">
                        <Loader2 className="w-5 h-5 animate-spin text-amber-deep" />
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <img src="/alan_chef.jpg" alt="Papi" className="w-10 h-10 rounded-full object-cover shrink-0" style={{ objectPosition: "62% 35%" }} />
                      <div className="space-y-2 max-w-[85%]">
                        <div
                          className="bg-cream border border-line px-4 py-3 rounded-2xl rounded-bl-md font-serif text-cocoa text-lg leading-relaxed"
                          data-testid={`ask-papi-a-${i}`}
                        >
                          {m.text}
                        </div>
                        {m.audio_url && (
                          <button
                            type="button"
                            onClick={() => playAudio(m.audio_url)}
                            className="text-sm font-serif italic text-amber-deep hover:underline inline-flex items-center gap-1"
                            data-testid={`ask-papi-replay-${i}`}
                          >
                            ▶ escuchar otra vez
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(); }}
              className="border-t border-line p-3 flex gap-2 items-end bg-paper"
            >
              <textarea
                data-testid="ask-papi-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                }}
                placeholder="Escríbele a Papi…"
                rows={1}
                className="flex-1 resize-none px-4 py-3 bg-cream border border-line rounded-2xl font-serif text-cocoa text-lg focus:outline-none focus:border-amber-deep max-h-32"
                style={{ minHeight: 52 }}
              />
              <button
                type="submit"
                disabled={!q.trim() || busy}
                data-testid="ask-papi-send-btn"
                className="p-3 rounded-full bg-amber-deep text-cream disabled:opacity-50 hover:scale-105 active:scale-95 transition"
                aria-label="Enviar"
              >
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </form>

            {/* Hidden audio player */}
            <audio ref={audioRef} preload="none" data-testid="ask-papi-audio" />
          </div>
        </div>
      )}
    </>
  );
}
